import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { GoogleLoginButton } from '@/components/auth/GoogleLoginButton';
import { AccountSelectionModal } from '@/components/auth/AccountSelectionModal';
import { useTranslation } from '@/hooks/useTranslation';
import { getVersionString } from '@/config/version';
import { authApi } from '@/api/auth';
import type { AccountOption } from '@/types/auth';
import logo from '@/assets/images/logo-sidebar.png';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle, isLoading, error, clearError } = useAuthStore();
  const { t } = useTranslation();

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  // Multi-account state
  const [showAccountSelection, setShowAccountSelection] = useState(false);
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const [pendingToken, setPendingToken] = useState<string>('');
  const [pendingUserName, setPendingUserName] = useState<string>('');
  const [isSelectingAccount, setIsSelectingAccount] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const handleAuthResponse = (response: any) => {
    if (response.requiresAccountSelection && response.accounts?.length > 1) {
      // Multi-account: show selection modal
      setAccountOptions(response.accounts);
      setPendingToken(response.pendingToken);
      setPendingUserName(response.user?.fullName || response.user?.email || '');
      setShowAccountSelection(true);
    } else {
      // Single account: proceed normally
      navigate(from, { replace: true });
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      clearError();
      const response = await authApi.login(data);
      
      if (response.requiresAccountSelection && response.accounts?.length) {
        setAccountOptions(response.accounts);
        setPendingToken(response.pendingToken || '');
        setPendingUserName(response.user?.fullName || response.user?.email || '');
        setShowAccountSelection(true);
      } else {
        // Single account — use the normal flow through the store
        await login(data);
        navigate(from, { replace: true });
      }
    } catch (error: any) {
      // Set error in the store so it renders in the UI
      const errorMessage = error.response?.data?.error
        || error.response?.data?.message
        || error.message
        || 'Falha ao fazer login';
      useAuthStore.setState({ error: errorMessage, isLoading: false });
    }
  };

  const handleAccountSelect = async (accountId: string) => {
    try {
      setIsSelectingAccount(true);
      const response = await authApi.selectAccount({ pendingToken, accountId });
      // Now complete login through the store with the full response
      const { handleAuthSuccessFromResponse } = useAuthStore.getState();
      handleAuthSuccessFromResponse(response);
      navigate(from, { replace: true });
    } catch (error: any) {
      useAuthStore.setState({ error: error.message || 'Falha ao selecionar conta' });
    } finally {
      setIsSelectingAccount(false);
    }
  };

  const handleGoogleSuccess = async (token: string) => {
    try {
      clearError();
      await loginWithGoogle({ token });
      navigate(from, { replace: true });
    } catch (error) {
      // Error is handled by the store
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: '#F6F6F8' }}
    >
      <div 
        className="w-full"
        style={{
          maxWidth: '450px',
          backgroundColor: '#FFF',
          borderRadius: '8px',
          padding: '48px 40px',
          boxShadow: '0px 4px 8px -4px rgba(50, 71, 92, 0.42)'
        }}
      >
        {/* Logo e Título */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src={logo} 
              alt="FeedBatch Logo" 
              style={{ 
                height: '120px',
                width: 'auto'
              }}
            />
          </div>
          <h2 
            className="text-2xl font-medium mb-2"
            style={{ color: '#22212C', fontWeight: 500 }}
          >
            {t('auth.signInTitle')}
          </h2>
          <p 
            className="text-sm"
            style={{ color: 'rgba(50, 71, 92, 0.6)' }}
          >
            {t('auth.signInSubtitle')}
          </p>
        </div>

        {/* Formulário */}
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <div 
              style={{
                backgroundColor: '#FDEDEF',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '1px solid #E5384F'
              }}
            >
              <p 
                className="text-sm"
                style={{ color: '#E5384F' }}
              >
                {error}
              </p>
            </div>
          )}

          <Input
            label={t('auth.email')}
            type="email"
            autoComplete="email"
            placeholder="seu@email.com.br"
            error={errors.email?.message}
            {...register('email')}
          />

          <Input
            label={t('auth.password')}
            type="password"
            autoComplete="current-password"
            error={errors.password?.message}
            {...register('password')}
          />

          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              style={{ 
                color: 'var(--wl-primary)',
                fontWeight: 500,
                textDecoration: 'none',
                fontSize: '0.875rem'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--wl-primary)'}
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>

          <Button type="submit" className="w-full" isLoading={isLoading}>
            {t('auth.loginButton')}
          </Button>

          {/* <div className="flex items-center justify-center gap-2 text-sm">
            <span style={{ color: 'rgba(50, 71, 92, 0.6)' }}>
              {t('auth.newToPlatform')}
            </span>
            <Link
              to="/register"
              style={{ 
                color: 'var(--wl-primary)',
                fontWeight: 500,
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--wl-primary)'}
            >
              {t('auth.createAccount')}
            </Link>
          </div> */}
        </form>

        {/* Divisor */}
        <div className="relative my-6">
          <div 
            className="absolute inset-0 flex items-center"
            style={{ height: '1px' }}
          >
            <div 
              className="w-full"
              style={{ backgroundColor: '#EDEDF5' }}
            />
          </div>
          <div className="relative flex justify-center">
            <span 
              className="px-3 text-sm"
              style={{ 
                backgroundColor: '#FFF',
                color: '#7A7796'
              }}
            >
              OU
            </span>
          </div>
        </div>

        {/* Google Login */}
        <GoogleLoginButton
          onSuccess={handleGoogleSuccess}
          onError={(error) => console.error('Google login error:', error)}
        />

        {/* Link para Planos - comentado
        <div className="text-center mt-6">
          <Link
            to="/plans"
            style={{ 
              color: 'var(--wl-primary)',
              fontSize: '0.875rem',
              textDecoration: 'none',
              fontWeight: 500
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--wl-primary)'}
          >
            {t('auth.viewPlans')}
          </Link>
        </div>
        */}

        {/* Versão */}
        <div className="text-center mt-4">
          <a
            href="https://github.com/Rompi-Project/FeedBatch-Web/blob/main/CHANGELOG.md"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: 'var(--wl-primary)',
              fontSize: '0.75rem',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--wl-primary)'}
          >
            {getVersionString()}
          </a>
        </div>

        {/* Links de Privacidade e Termos */}
        <div className="text-center mt-4 flex justify-center gap-3 text-xs" style={{ color: 'rgba(50, 71, 92, 0.6)' }}>
          <a
            href="/politica-de-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: 'rgba(50, 71, 92, 0.6)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(50, 71, 92, 0.6)'}
          >
            Política de Privacidade
          </a>
          <span>•</span>
          <a
            href="/termos-de-servico"
            target="_blank"
            rel="noopener noreferrer"
            style={{ 
              color: 'rgba(50, 71, 92, 0.6)',
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--wl-accent)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(50, 71, 92, 0.6)'}
          >
            Termos de Serviço
          </a>
        </div>
      </div>

      {/* Account Selection Modal */}
      {showAccountSelection && (
        <AccountSelectionModal
          accounts={accountOptions}
          userName={pendingUserName}
          onSelect={handleAccountSelect}
          onCancel={() => setShowAccountSelection(false)}
          isLoading={isSelectingAccount}
        />
      )}
    </div>
  );
}
